import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cajasAPI, formasPagoAPI, usuariosAPI } from '../services/api';
import { toast } from 'react-toastify';
import UsuariosAccesoSelector from '../components/UsuariosAccesoSelector';
import '../Clientes.css';

const CajaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    saldo_inicial: '',
    monto_maximo_salida: '',
    moneda: 'ARS',
    activa: true,
    mostrar_saldo_dashboard: false,
    usuario_ids: [],
  });

  const loadUsuarios = useCallback(async () => {
    try {
      const res = await usuariosAPI.getActivos();
      if (res.success && Array.isArray(res.data)) setUsuarios(res.data);
    } catch {
      setUsuarios([]);
    }
  }, []);

  const loadCaja = useCallback(async () => {
    try {
      const response = await cajasAPI.getById(id);
      if (response.success) {
        const d = response.data;
        setFormData({
          nombre: d.nombre || '',
          descripcion: d.descripcion || '',
          saldo_inicial: d.saldo_inicial != null ? d.saldo_inicial : '',
          monto_maximo_salida: d.monto_maximo_salida != null ? d.monto_maximo_salida : '',
          moneda: d.moneda || 'ARS',
          activa: Boolean(d.activa),
          mostrar_saldo_dashboard: Boolean(d.mostrar_saldo_dashboard),
          usuario_ids: Array.isArray(d.usuario_ids) ? d.usuario_ids.map(String) : [],
        });
      }
    } catch (error) {
      toast.error('Error al cargar caja');
      navigate('/cajas');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadUsuarios();
    if (id) {
      loadCaja();
    }
  }, [id, loadCaja, loadUsuarios]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      saldo_inicial: formData.saldo_inicial === '' ? 0 : parseFloat(formData.saldo_inicial),
      monto_maximo_salida: formData.monto_maximo_salida === '' ? null : parseFloat(formData.monto_maximo_salida),
      usuario_ids: formData.usuario_ids.map((x) => parseInt(x, 10)).filter((n) => !isNaN(n)),
    };

    if (id && payload.activa === false) {
      try {
        const resFormas = await formasPagoAPI.getAll({ caja_id: id });
        const formas = (resFormas.success && resFormas.data) ? resFormas.data : [];
        if (formas.length > 0) {
          const nombres = formas.map(fp => fp.nombre).join(', ');
          const confirmar = window.confirm(
            `Al desactivar esta caja también se desactivarán las siguientes formas de pago: ${nombres}.\n\n¿Desea continuar?`
          );
          if (!confirmar) return;
        }
      } catch (err) {
        toast.error('No se pudo verificar las formas de pago asociadas.');
        return;
      }
    }

    setLoading(true);
    try {
      let response;
      if (id) {
        response = await cajasAPI.update(id, payload);
      } else {
        response = await cajasAPI.create(payload);
      }

      if (response.success) {
        toast.success(id ? 'Caja actualizada exitosamente' : 'Caja creada exitosamente');
        const formasPago = response.formas_pago_que_la_utilizan;
        if (id && payload.activa === false && formasPago && formasPago.length > 0) {
          const nombres = formasPago.map(fp => fp.nombre).join(', ');
          toast.info(`Se desactivaron también las formas de pago: ${nombres}.`, { autoClose: 6000 });
        }
        navigate('/cajas');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar caja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cliente-form">
      <h1>{id ? 'Editar Caja' : 'Nueva Caja'}</h1>
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
              placeholder="Ej: Caja Principal, Caja Chica"
            />
          </div>

          <div className="form-group">
            <label>Descripción</label>
            <input
              type="text"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción opcional"
            />
          </div>

          <div className="form-group">
            <label>Saldo inicial</label>
            <input
              type="number"
              name="saldo_inicial"
              value={formData.saldo_inicial}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label>Monto máximo por egreso</label>
            <input
              type="number"
              name="monto_maximo_salida"
              value={formData.monto_maximo_salida}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="Sin límite"
            />
            <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
              Tope por operación de salida de fondos desde esta caja. Vacío = sin límite.
            </small>
          </div>

          <div className="form-group">
            <label>Moneda *</label>
            <select
              name="moneda"
              value={formData.moneda || 'ARS'}
              onChange={handleChange}
              required
            >
              <option value="ARS">ARS - Peso argentino</option>
              <option value="USD">USD - Dólar estadounidense</option>
              <option value="EUR">EUR - Euro</option>
            </select>
          </div>

          <UsuariosAccesoSelector
            usuarios={usuarios}
            selectedIds={formData.usuario_ids}
            onChange={(usuario_ids) => setFormData((prev) => ({ ...prev, usuario_ids }))}
            hintText="Si no asigna usuarios, solo el administrador podrá operar esta caja."
          />

          <div className="form-group">
            <label className="form-toggle-wrap">
              <span className="toggle-switch-box">
                <input
                  type="checkbox"
                  name="activa"
                  checked={formData.activa}
                  onChange={handleChange}
                />
                <span className="form-toggle-switch" />
              </span>
              <span className="form-toggle-label">{formData.activa ? 'Desactivar' : 'Activar'}</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-toggle-wrap">
              <span className="toggle-switch-box">
                <input
                  type="checkbox"
                  name="mostrar_saldo_dashboard"
                  checked={formData.mostrar_saldo_dashboard}
                  onChange={handleChange}
                />
                <span className="form-toggle-switch" />
              </span>
              <span className="form-toggle-label">Mostrar saldo en el Dashboard</span>
            </label>
            <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
              Si está activo, en el Dashboard se verá el saldo inicial a fecha desde, movimientos del período y saldo final.
            </small>
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/cajas')} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CajaForm;
